'use client';

import { Button } from '@eliadmin/core/src/components/ui/button';
import { useCarouselContext } from '@eliadmin/core/src/components/ui/carousel-context';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import type { EmblaCarouselType } from 'embla-carousel-react';
import useEmblaCarousel, { type EmblaOptionsType } from 'embla-carousel-react';
import type { RefObject } from 'react';
import { useEffect, useImperativeHandle, useRef, useState } from 'react';
import { LuChevronDown, LuChevronUp, LuDot, LuMoveLeft, LuMoveRight } from 'react-icons/lu';
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from 'react-icons/md';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

type CarouselClassName = {
  wrapper?: string;
  container?: string;
  slide?: string;
  arrows?: string;
  dots?: string;
  dot?: string;
  customDots?: string;
  customDot?: string;
  main?: string;
  thumbnail?: string;
  thumbnails?: string;
  thumbnailsWrapper?: string;
  /**TODO: dorobit classes */
};

type CarouselCustomDots =
  | {
      showCustomDots: boolean;
      customDots: React.ReactNode[];
      colors: {
        activeColor: string;
        inactiveColor: string;
      };
    }
  | undefined;

export type CarouselProps = {
  className?: CarouselClassName;
  slides: React.ReactNode[];
  options?: EmblaOptionsType;
  customDots?: CarouselCustomDots;
  // styles
  showArrows?: boolean;
  showDots?: boolean;
  showThumbnails?: boolean;
  showThumbnailsArrows?: boolean;
  enableInterval?: boolean;
  intervalLength?: number;
  enableLoop?: boolean;
  openLightbox?: () => void;
  showMoreButton?: boolean;
  allImagesCount?: number;
  emblaRef?: RefObject<EmblaCarouselType>;
  customTransition?: boolean;
  onSelectedIndex?: (index: number) => void;
  initialSelectedIndex?: number;
};

export function Carousel(props: CarouselProps) {
  const { t } = useTranslations();
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: !!props.enableLoop,
    ...props.options,
  });
  const thumbsRef = useRef<HTMLDivElement | null>(null);
  const dotsRef = useRef<HTMLDivElement | null>(null);
  const autoPlayIntervalRef = useRef<any>(null);

  // imperative handle of emblaApi
  useImperativeHandle(props.emblaRef, () => emblaApi!, [emblaApi]);

  const carouselProviderContext = useCarouselContext();

  const [selectedIndex, setSelectedIndex] = useState(props.initialSelectedIndex || 0);

  const onPrev = () => emblaApi?.scrollPrev();
  const onNext = () => emblaApi?.scrollNext();

  const isContextDefined = !!carouselProviderContext;

  useEffect(() => {
    props.onSelectedIndex?.(selectedIndex);
  }, [selectedIndex]);

  useEffect(() => {
    if (!emblaApi) return;

    const startAutoPlay = () => {
      stopAutoPlay(); // Clear existing interval
      autoPlayIntervalRef.current = setInterval(() => {
        if (emblaApi) {
          emblaApi.scrollNext();
        }
      }, props.intervalLength || 5000);
    };

    const stopAutoPlay = () => {
      if (autoPlayIntervalRef.current) {
        clearInterval(autoPlayIntervalRef.current);
        autoPlayIntervalRef.current = null;
      }
    };

    if (props.enableInterval) {
      // emblaApi.on('pointerDown', stopAutoPlay);
      // emblaApi.on('pointerUp', startAutoPlay);
      startAutoPlay();
    }

    return () => {
      // if (props.enableInterval) {
      //   emblaApi.off('pointerDown', stopAutoPlay);
      //   emblaApi.off('pointerUp', startAutoPlay);
      // }
      stopAutoPlay();
    };
  }, [emblaApi, props.enableInterval, props.intervalLength]);

  useEffect(() => {
    if (!emblaApi) return;

    const onSelect = () => {
      setSelectedIndex(emblaApi.selectedScrollSnap());
      if (!isContextDefined) return;
      carouselProviderContext?.setSelectedIndex(emblaApi.selectedScrollSnap());
      carouselProviderContext?.setCanGoNext(emblaApi.canScrollNext());
      carouselProviderContext?.setCanGoPrev(emblaApi.canScrollPrev());
    };

    if (isContextDefined) carouselProviderContext?.setApi(emblaApi);
    emblaApi.on('select', onSelect);
    onSelect();

    return () => {
      emblaApi.off('select', onSelect);
      if (isContextDefined) carouselProviderContext?.setApi(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    emblaApi,
    /** IMPORTANT: only check against isContextDefined, because we want to prevent infinite loop */
    isContextDefined,
  ]);

  useEffect(() => {
    if (!isContextDefined) return;
    carouselProviderContext?.setSlideCount(props.slides.length);
    /** IMPORTANT: only check against isContextDefined, because we want to prevent infinite loop */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isContextDefined, props.slides.length]);

  const updateNavigationState = () => {
    if (!emblaApi) return;
    carouselProviderContext?.setSelectedIndex(emblaApi.selectedScrollSnap());
    carouselProviderContext?.setCanGoNext(emblaApi.canScrollNext());
    carouselProviderContext?.setCanGoPrev(emblaApi.canScrollPrev());
  };

  // Effect for updating canGoPrev and canGoNext based on selectedIndex and slideCount
  useEffect(() => {
    updateNavigationState();
    // Listen to resize events to update state based on viewport width
    const handleResize = () => {
      updateNavigationState(); // Recalculate canGoPrev and canGoNext on resize
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carouselProviderContext?.selectedIndex, carouselProviderContext?.slideCount]); // Ensure to list all dependencies here

  const [isTransitioning, setIsTransitioning] = useState(false);

  useEffect(() => {
    if (!emblaApi) return;

    const onTransitionStart = () => {
      setIsTransitioning(true);
    };

    const onTransitionEnd = () => {
      setIsTransitioning(false);
    };

    emblaApi.on('scroll', onTransitionStart);
    emblaApi.on('settle', onTransitionEnd);

    return () => {
      emblaApi.off('scroll', onTransitionStart);
      emblaApi.off('settle', onTransitionEnd);
    };
  }, [emblaApi]);




  const scrollThumbs = (delta: number) => {
    if (thumbsRef.current) {
      const container = thumbsRef.current;
      const currentScrollTop = container.scrollTop;
      container.scrollTo({
        top:
          delta < 0 && props.options?.loop === true && selectedIndex === 0
            ? container.scrollWidth
            : delta > 0 && props.options?.loop === true && selectedIndex === props.slides.length - 1
              ? 0
              : currentScrollTop + delta,
        behavior: 'smooth',
      });
    }
  };

  const scrollDots = (delta: number) => {
    if (dotsRef.current) {
      const container = dotsRef.current;
      const currentScrollLeft = container.scrollLeft;
      container.scrollTo({
        left:
          delta < 0 && props.options?.loop === true && selectedIndex === 0
            ? container.scrollWidth
            : delta > 0 && props.options?.loop === true && selectedIndex === props.slides.length - 1
              ? 0
              : currentScrollLeft + delta,
        behavior: 'smooth',
      });
    }
  };

  const slides = props.slides.map((slide, index) => {
    return (
      <div
        className={cn(
          'h-full w-full flex-shrink-0 flex-grow-0',
          'ea-carousel__slide',
          props.customTransition && isTransitioning ? 'ea-carousel__slide--zoom' : '',
          props.className?.slide,
        )}
        key={index}
      >
        {slide}
      </div>
    );
  });


  const handleThumbClick = (index: number) => {
    const indexDelta = index - selectedIndex;

    emblaApi?.scrollTo(index);
    scrollDots(indexDelta * 32);
  };

  const thumbs = props.slides.map((slide, index) => {
    return (
      <div
        className={cn(
          'min-h-[48px] w-12 cursor-pointer rounded-sm bg-muted',
          'ea-carousel__thumb',
          index === selectedIndex ? 'border border-foreground' : '',
          props.className?.thumbnail,
        )}
        key={index}
        onClick={() => handleThumbClick(index)}
      >
        {slide}
      </div>
    );
  });

  return (
    <div className={cn('flex w-full gap-1', 'ea-carousel__wrapper', props.className?.wrapper)}>
      {props.showThumbnails && (
        <div
          className={cn(
            'hidden h-full max-h-[500px] min-w-[56px] max-w-[56px] flex-col gap-2 md:flex',
            'ea-carousel__thumbnails',
            props.className?.thumbnailsWrapper,
          )}
        >
          {props.showThumbnailsArrows && (
            <Button
              className={cn(
                'h-5 rounded-sm',
                'ea-carousel__thumbnail-control ea-carousel__thumbnail-control-up',
              )}
              variant="secondary"
              disabled={selectedIndex === 0 && props?.options?.loop === false}
              onClick={() => {
                onPrev(), scrollThumbs(-56);
                scrollDots(-32);
              }}
            >
              <LuChevronUp size={16} />
            </Button>
          )}
          <div
            ref={thumbsRef}
            className={cn(
              'hide-scroll-bar flex min-w-[56px] max-w-[56px] flex-col items-center justify-start gap-2 overflow-hidden rounded-sm',
              'ea-carousel__thumbnail',
              props.className?.thumbnails,
            )}
          >
            {thumbs}
            {props.showMoreButton && (
              <div
                className={cn(
                  'flex min-h-[48px] w-12 cursor-pointer items-center justify-center rounded-lg bg-muted',
                  'ea-carousel__thumb',
                  props.className?.thumbnail,
                  'bg-primary text-background',
                )}
                onClick={() => props.openLightbox?.()}
              >
                {props.allImagesCount ? `+${props.allImagesCount - 4}` : 'Zobraziť viac'}
              </div>
            )}
          </div>
          {props.showThumbnailsArrows && (
            <Button
              className={cn(
                'h-5 rounded-sm',
                'ea-carousel__thumbnail-control ea-carousel__thumbnail-control-down',
              )}
              variant="secondary"
              disabled={selectedIndex === slides.length - 1 && props?.options?.loop === false}
              onClick={() => {
                onNext(), scrollThumbs(56);
                scrollDots(32);
              }}
            >
              <LuChevronDown size={16} />
            </Button>
          )}
        </div>
      )}
      <div
        className={cn('relative overflow-hidden', 'ea-carousel', props.className?.main)}
        ref={emblaRef}
      >
        <div
          className={cn('flex h-full ', 'ea-carousel__container', props.className?.container)}
          onClick={props.openLightbox}
        >
          {slides}
        </div>
        {props.showArrows && (
          <>
            <button
              className={cn(
                'absolute left-2 top-[calc(50%-8px)] rounded-md bg-gray-100 bg-opacity-80 p-1 text-foreground disabled:opacity-50',
                'ea-carousel__control ea-carousel__control--left',
                props.className?.arrows,
              )}
              disabled={selectedIndex === 0 && props?.options?.loop === false}
              onClick={() => {
                onPrev(), scrollThumbs(-56);
                scrollDots(-32);
              }}
            >
              <MdKeyboardArrowLeft className="h-4 w-4" />
            </button>
            <button
              className={cn(
                'absolute right-2 top-[calc(50%-8px)] rounded-md bg-gray-100 bg-opacity-80 p-1 text-foreground disabled:opacity-50',
                'ea-carousel__control ea-carousel__control--right',
                props.className?.arrows,
              )}
              disabled={selectedIndex === slides.length - 1 && props?.options?.loop === false}
              onClick={() => {
                onNext(), scrollThumbs(56);
                scrollDots(32);
              }}
            >
              <MdKeyboardArrowRight className="h-4 w-4" />
            </button>
          </>
        )}
        {props.showDots && (
          <div
            className={cn(
              'absolute bottom-4 flex w-full items-center justify-center',
              'ea-carousel__dots__container',
              props.className?.dots,
            )}
          >
            <div
              className={cn(
                'flex max-w-[160px] cursor-pointer justify-start overflow-hidden',
                'ea-carousel__dots__wrapper',
              )}
              ref={dotsRef}
            >
              {slides.map((_, index) => (
                <LuDot
                  key={index}
                  className={cn(
                    `h-10 w-10 ${
                      index === selectedIndex ? 'text-foreground' : 'text-muted-foreground'
                    }`,
                    'ea-carousel__dot',
                    props.className?.dot,
                  )}
                  onClick={() => emblaApi?.scrollTo(index)}
                />
              ))}
            </div>
          </div>
        )}
        {props.customDots?.showCustomDots && (
          <div
            className={cn(
              'flex w-full items-center justify-center',
              'ea-carousel__custom-dots__container',
              props.className?.customDots,
            )}
          >
            <div
              className={cn('flex w-full cursor-pointer', 'ea-carousel__custom-dots__wrapper')}
              ref={dotsRef}
            >
              {props.customDots.customDots.map((dot, index) => (
                <div
                  key={index}
                  className={cn(
                    `h-full w-full ${
                      index === selectedIndex
                        ? props.customDots?.colors.activeColor
                        : props.customDots?.colors.inactiveColor
                    }`,
                    props.className?.customDot,
                    'ea-carousel__custom-dot',
                  )}
                  onClick={() => emblaApi?.scrollTo(index)}
                >
                  <div
                    className={cn(
                      'absolute left-1/2 top-[-8px] border-solid',
                      'ea-carousel__custom-dot-extra',
                    )}
                    style={{
                      content: '""',
                      transform: 'translateX(-50%)',
                      borderWidth: '0 5px 8px 5px',
                      borderColor: `transparent transparent ${
                        index === selectedIndex ? 'hsl(var(--accent))' : 'hsl(var(--secondary))'
                      } transparent`,
                    }}
                  />
                  {dot}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
